// Utils
import { describeExamTypes } from "src/utils/emr/examTypeHelper";
import {
  Translate,
  describeWorklistTiming,
} from "src/utils/emr/worklistTimingHelper";

// Types
import { EMRConnectionType } from "src/services/emr/types";

export const PRIOR_HISTORY_LABELS: Record<number, string> = {
  0: "nothing earlier",
  6: "the last 6 months",
  12: "the last year",
  24: "the last 2 years",
  60: "the last 5 years",
  240: "all of it",
};

export const describePriorHistory = (
  months: number | undefined,
  t: Translate
) => {
  if (months === undefined) return t("all earlier exams for a new patient");
  if (months === 0) return t("no earlier exams for a new patient");

  const label = PRIOR_HISTORY_LABELS[months];
  return label
    ? t("{{period}} for a new patient", { period: t(label) })
    : t("the last {{months}} months for a new patient", { months });
};

export const getJobTitle = (connection: EMRConnectionType) => {
  if (connection.sourceKind === "Worklist") return "Worklist";
  if (isReportDestination(connection)) return "Reports";
  return "Exams";
};

export const isReportDestination = (connection: EMRConnectionType) =>
  connection.sourceKind === "DicomStore" ||
  connection.sourceKind === "ExportFolder";

export const importsExams = (connection: EMRConnectionType) =>
  connection.sourceKind !== "Worklist" && !isReportDestination(connection);

export const getJobDirection = (connection: EMRConnectionType) =>
  isReportDestination(connection) ? "Out" : "In";

export const describeReportFormats = (
  reportTypes: Array<string> | undefined,
  t: Translate
) =>
  (reportTypes ?? [])
    .map((reportType) => (reportType === "Pdf" ? t("PDF") : t("DICOM")))
    .join(t(" and "));

export const describeExportFrom = (
  exportFrom: string | undefined,
  t: Translate
) =>
  exportFrom
    ? t("exams from {{date}} on", {
        date: new Date(exportFrom).toLocaleDateString(),
      })
    : undefined;

const joinParts = (parts: Array<string | undefined>) =>
  parts.filter(Boolean).join(" · ");

export const describeConnectionJob = (
  connection: EMRConnectionType,
  t: Translate
) => {
  const config = connection.config ?? {};

  switch (connection.sourceKind) {
    case "SharedFolder":
      return connection.deviceKey
        ? t("Files from {{path}}", { path: config.path ?? "" })
        : t("Picked up as files land · type read from each file");
    case "Worklist":
      return joinParts([
        config.mode === "CsvFolder" ? t("Worklist CSV") : undefined,
        describeWorklistTiming(config, t),
        config.lookaheadDays
          ? t("today and the next {{days}} days", {
              days: config.lookaheadDays,
            })
          : t("today's appointments"),
      ]);
    case "DicomListener":
      return joinParts([
        describeExamTypes(config.examTypes, t) || t("Any exam"),
        connection.pacServerId ? t("pushed to the hub") : t("sends over DICOM"),
      ]);
    case "DicomQueryRetrieve":
      return joinParts([
        t("Only for patients on the worklist"),
        describeExamTypes(config.examTypes, t) || undefined,
        describePriorHistory(config.priorHistoryMonths, t),
      ]);
    case "DicomStore":
      return joinParts([
        t("RetinaLogik reports stored back"),
        describeExportFrom(config.exportFrom, t),
      ]);
    case "ExportFolder":
      return joinParts([
        t("{{formats}} reports written to {{path}}", {
          formats: describeReportFormats(config.reportTypes, t),
          path: config.path ?? "",
        }),
        describeExportFrom(config.exportFrom, t),
      ]);
    default:
      return "";
  }
};

export const describeDicomAddress = (
  config: EMRConnectionType["config"] | undefined
) =>
  config?.host
    ? joinParts([
        `${config.host} : ${config.port ?? ""}`,
        config.remoteAeTitle ? `AE ${config.remoteAeTitle}` : undefined,
      ])
    : "";
